import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { vocabularyApi } from '../../utils/api';

// Only the saved-data request blocks the initial view. All external work is
// optional follow-up, and never owns private editable state or loading chrome.
export function useWordEntry({ languageCode, interfaceLang = 'en' }) {
  const { user } = useAuth();
  const [wordData, updateData] = useState(null);
  const [wordLoading, setWordLoading] = useState(false);
  const generation = useRef(0);
  const controller = useRef(null);

  const cancel = useCallback(() => {
    generation.current += 1;
    controller.current?.abort();
    controller.current = null;
  }, []);

  useEffect(() => {
    cancel();
    updateData(null);
    setWordLoading(false);
    return cancel;
  }, [languageCode, interfaceLang, user?.user_id, cancel]);

  // An explicit edit/restore wins over any older enrichment response.
  const setWordData = useCallback(value => {
    cancel();
    setWordLoading(false);
    updateData(previous => {
      const next = typeof value === 'function' ? value(previous) : value;
      return next ? { ...next, _details_loading: false } : next;
    });
  }, [cancel]);

  const loadEntry = useCallback(async (term, isExpression = false, lookupLanguage = languageCode) => {
    if (!term || !lookupLanguage) return null;
    cancel();
    const ticket = generation.current;
    const abort = new AbortController();
    controller.current = abort;
    const current = () => generation.current === ticket && !abort.signal.aborted;
    const get = (cacheOnly) => vocabularyApi.getWordData(term, lookupLanguage, isExpression, false, interfaceLang, {
      signal: abort.signal, timeout: cacheOnly ? 12000 : 45000, params: { cache_only: cacheOnly },
    });
    setWordLoading(true);
    updateData(null);
    try {
      const { data } = await get(true);
      if (!current()) return null;
      const needsDetails = data.lookup_pending || data.translation_pending;
      updateData({ ...data, _details_loading: !!needsDetails });
      setWordLoading(false);

      if (needsDetails) {
        // Intentionally not awaited: saved definitions/status/notes are usable now.
        (async () => {
          let latest = data;
          let failed = false;
          try {
            if (data.lookup_pending || data.translation_pending) {
              latest = (await get(false)).data;
              if (!current()) return;
              updateData({ ...latest, _details_loading: true });
            }
            // Missing dictionary entries stay editable; AI enrichment is an
            // explicit action, not an extra job on every uncached word click.
            failed = !!latest.translation_pending || (!latest.locked && !latest.definitions?.length);
            if (current()) updateData({ ...latest, _details_loading: false, _details_error: failed });
          } catch {
            if (current()) updateData(previous => previous ? { ...previous, _details_loading: false, _details_error: true } : previous);
          }
        })();
      }
      return data;
    } catch {
      if (current()) {
        updateData({ word: term, normalized: term, definitions: [], error: true });
        setWordLoading(false);
      }
      return null;
    }
  }, [languageCode, interfaceLang, cancel]);

  return { wordData, setWordData, wordLoading, loadEntry, cancel };
}